/**
 * Pure GenerateText skill compatibility helpers (no network / api imports).
 */

import {
  GENERATE_TEXT_SKILL_GENERAL,
  GENERATE_TEXT_SKILL_OUTFIT,
  GENERATE_TEXT_SKILL_TEXT,
  inferDefaultGenerateTextSkillKey,
} from './generateTextContract'

/** Mirror backend `_IMAGE_REQUIRED_TEXT_SKILLS` — prompt-only must not bind here. */
export const GENERATE_TEXT_IMAGE_REQUIRED_SKILL_KEYS = new Set<string>([
  GENERATE_TEXT_SKILL_GENERAL,
  GENERATE_TEXT_SKILL_OUTFIT,
  'fashion_outfit_professional_critique',
  'fashion_outfit_optimization_advisor',
])

/** Minimal skill shape shared by API DTO and client cache. */
export interface GenerateTextSkillLike {
  skill_key: string
  skill_version: string
  display_name: string
  description?: string | null
  accepted_input_types: string[]
  suggested_instructions?: string[] | null
  platforms?: string[] | null
  is_default?: boolean
  ownership_type?: string | null
  ownership_label?: string | null
  skill_definition_id?: string | null
  skill_version_id?: string | null
}

/** Normalize backend skill_id/version catalog rows into client skill_key shape. */
export function normalizeTextGenerationSkillDescriptor(raw: Record<string, unknown>): GenerateTextSkillLike | null {
  const skillKey =
    (typeof raw.skill_key === 'string' && raw.skill_key.trim()) ||
    (typeof raw.skill_id === 'string' && raw.skill_id.trim() && !String(raw.skill_id).includes('-')
      ? String(raw.skill_id).trim()
      : '') ||
    ''
  if (!skillKey) return null
  const skillVersion =
    (typeof raw.skill_version === 'string' && raw.skill_version.trim()) ||
    (typeof raw.version === 'string' && raw.version.trim()) ||
    'v1'
  return {
    skill_key: skillKey,
    skill_version: skillVersion,
    display_name:
      (typeof raw.display_name === 'string' && raw.display_name.trim()) || skillKey,
    description: typeof raw.description === 'string' ? raw.description : null,
    accepted_input_types: Array.isArray(raw.accepted_input_types)
      ? (raw.accepted_input_types as string[])
      : ['image'],
    suggested_instructions: Array.isArray(raw.suggested_instructions)
      ? (raw.suggested_instructions as string[])
      : null,
    platforms: Array.isArray(raw.platforms) ? (raw.platforms as string[]) : null,
    is_default: Boolean(raw.is_default),
    ownership_type: typeof raw.ownership_type === 'string' ? raw.ownership_type : null,
    ownership_label: typeof raw.ownership_label === 'string' ? raw.ownership_label : null,
    skill_definition_id: typeof raw.skill_definition_id === 'string' ? raw.skill_definition_id : null,
    skill_version_id: typeof raw.skill_version_id === 'string' ? raw.skill_version_id : null,
  }
}

/** Normalize accepted_input_types entry (image / IMAGE / image_asset → image). */
export function normalizeAcceptedInputType(raw: unknown): string {
  return String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]/g, '')
    .replace(/asset$/, '')
}

export function skillAcceptsImageInput(skill: GenerateTextSkillLike): boolean {
  const types = Array.isArray(skill.accepted_input_types) ? skill.accepted_input_types : []
  if (types.length === 0) return true
  return types.some((t) => {
    const n = normalizeAcceptedInputType(t)
    return n === 'image' || n === 'img' || n === 'picture' || n === 'photo'
  })
}

export function skillSupportsPromptOnlyInput(skill: GenerateTextSkillLike): boolean {
  const types = Array.isArray(skill.accepted_input_types) ? skill.accepted_input_types : []
  if (types.length === 0) return false
  return types.some((t) => normalizeAcceptedInputType(t) === 'text')
}

export function skillEligibleForPromptOnlyGenerateText(skill: GenerateTextSkillLike): boolean {
  if (GENERATE_TEXT_IMAGE_REQUIRED_SKILL_KEYS.has(skill.skill_key)) return false
  return skillSupportsPromptOnlyInput(skill)
}

/**
 * Compatibility filter for V1 image→text path.
 * Preserves catalog order; never dedupes by platform.
 */
export function filterImageCompatibleTextSkills<T extends GenerateTextSkillLike>(skills: T[]): T[] {
  return skills.filter(skillAcceptsImageInput)
}

export function findTextGenerationSkill<T extends GenerateTextSkillLike>(
  skills: T[],
  skillKey: string | null | undefined,
): T | null {
  const key = typeof skillKey === 'string' ? skillKey.trim() : ''
  if (!key) return null
  return skills.find((s) => s.skill_key === key) ?? null
}

/** Resolve effective skill for AUTO vs EXPLICIT selection. */
export function resolveGenerateTextSkill<T extends GenerateTextSkillLike>(args: {
  skillSelectionSource: string | null | undefined
  skillKey: string | null | undefined
  skills: T[]
  sourceNodeType?: string | null
  preferredSkillKey?: string | null
  /** Ready image count on the node (0 = prompt-only path). */
  imageInputCount?: number
}): T | null {
  const imageCount = typeof args.imageInputCount === 'number' ? args.imageInputCount : 0
  const pool =
    imageCount > 0
      ? filterImageCompatibleTextSkills(args.skills)
      : args.skills.filter(skillEligibleForPromptOnlyGenerateText)
  const source = String(args.skillSelectionSource || 'AUTO').toUpperCase()
  if (source === 'EXPLICIT') {
    return findTextGenerationSkill(pool.length ? pool : args.skills, args.skillKey)
  }
  const preferredKey =
    (typeof args.preferredSkillKey === 'string' && args.preferredSkillKey.trim()) ||
    inferDefaultGenerateTextSkillKey(args.sourceNodeType)
  const preferred = findTextGenerationSkill(imageCount > 0 ? pool : args.skills, preferredKey)
  if (
    preferred &&
    (imageCount > 0
      ? skillAcceptsImageInput(preferred)
      : skillEligibleForPromptOnlyGenerateText(preferred))
  ) {
    return preferred
  }
  if (imageCount === 0) {
    const textKeyHit = findTextGenerationSkill(args.skills, GENERATE_TEXT_SKILL_TEXT)
    if (textKeyHit) return textKeyHit
    const textDefault = pool.find((s) => s.is_default) ?? pool[0]
    if (textDefault) return textDefault
  }
  const bindingDefault = pool.find((s) => s.is_default)
  if (bindingDefault) return bindingDefault
  return pool[0] ?? null
}
